import React, { useState } from 'react';
import { uploadChapter, updateChapter, deleteChapter, reorderChapters } from '../services/api';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaEdit, FaGripVertical, FaFilePdf, FaSpinner } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './ChapterManager.css';

const ChapterManager = ({ courseId, chapters, onChaptersUpdate }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingChapterId, setEditingChapterId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chapterData, setChapterData] = useState({
    title: '',
    pdfFile: null,
    pageCount: 0
  });

  const handleAddChapter = async (e) => {
    e.preventDefault();

    if (!chapterData.title || !chapterData.pdfFile) {
      toast.error('Please provide chapter title and PDF file');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('chapter', chapterData.pdfFile);
      formData.append('title', chapterData.title);
      formData.append('pageCount', chapterData.pageCount || 0);
      formData.append('order', chapters.length + 1);

      const response = await uploadChapter(courseId, formData);

      toast.success('Chapter added successfully!');
      setChapterData({ title: '', pdfFile: null, pageCount: 0 });
      setIsAdding(false);

      // Refresh chapters
      if (onChaptersUpdate) {
        onChaptersUpdate();
      }
    } catch (error) {
      console.error('Error adding chapter:', error);
      toast.error(error.response?.data?.message || 'Failed to add chapter');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    if (!window.confirm('Are you sure you want to delete this chapter?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteChapter(courseId, chapterId);
      toast.success('Chapter deleted successfully!');

      if (onChaptersUpdate) {
        onChaptersUpdate();
      }
    } catch (error) {
      console.error('Error deleting chapter:', error);
      toast.error(error.response?.data?.message || 'Failed to delete chapter');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) {
      return; // Dropped outside the list
    }

    if (result.source.index === result.destination.index) {
      return; // No movement
    }

    try {
      setLoading(true);

      // Create a copy of the chapters array and reorder it
      const reorderedChapters = Array.from(chapters);
      const [movedChapter] = reorderedChapters.splice(result.source.index, 1);
      reorderedChapters.splice(result.destination.index, 0, movedChapter);

      // Create the reorder data with new orders
      const reorderData = reorderedChapters.map((chapter, index) => ({
        chapterId: chapter._id,
        order: index + 1
      }));

      await reorderChapters(courseId, { chapters: reorderData });
      toast.success('Chapter reordered successfully!');

      if (onChaptersUpdate) {
        onChaptersUpdate();
      }
    } catch (error) {
      console.error('Error reordering chapter:', error);
      toast.error(error.response?.data?.message || 'Failed to reorder chapter');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chapter-manager">
      <div className="chapter-manager-header">
        <h3>Chapters ({chapters.length})</h3>
        <button
          type="button"
          className="btn-add-chapter"
          onClick={() => setIsAdding(!isAdding)}
          disabled={loading}
        >
          <FaPlus /> {isAdding ? 'Cancel' : 'Add Chapter'}
        </button>
      </div>

      {isAdding && (
        <div className="add-chapter-form">
          <div className="form-group">
            <label>Chapter Title *</label>
            <input
              type="text"
              value={chapterData.title}
              onChange={(e) => setChapterData({ ...chapterData, title: e.target.value })}
              placeholder="e.g., Introduction to Trading"
              onKeyPress={(e) => e.key === 'Enter' && handleAddChapter(e)}
              required
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>PDF File *</label>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => setChapterData({ ...chapterData, pdfFile: e.target.files[0] })}
                required
                disabled={loading}
              />
              {chapterData.pdfFile && (
                <small className="file-info">
                  <FaFilePdf /> {chapterData.pdfFile.name}
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Page Count (Optional)</label>
              <input
                type="number"
                value={chapterData.pageCount}
                onChange={(e) => setChapterData({ ...chapterData, pageCount: parseInt(e.target.value) || 0 })}
                placeholder="0"
                min="0"
                onKeyPress={(e) => e.key === 'Enter' && handleAddChapter(e)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={handleAddChapter}
              disabled={loading}
            >
              {loading ? <FaSpinner className="spinner" /> : <FaPlus />}
              {loading ? 'Adding...' : 'Add Chapter'}
            </button>
          </div>
        </div>
      )}

      {chapters.length === 0 ? (
        <div className="empty-state">
          <FaFilePdf size={48} />
          <p>No chapters yet. Add your first chapter to get started.</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="chapters">
            {(provided, snapshot) => (
              <div
                className={`chapters-list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {chapters.map((chapter, index) => (
                  <Draggable key={chapter._id} draggableId={chapter._id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        className={`chapter-item ${snapshot.isDragging ? 'dragging' : ''}`}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <div className="drag-handle" {...provided.dragHandleProps}>
                          <FaGripVertical />
                        </div>

                        <div className="chapter-info">
                          <div className="chapter-number">
                            Chapter {index + 1}
                          </div>
                          <div className="chapter-details">
                            <h4>{chapter.title}</h4>
                            <div className="chapter-meta">
                              {chapter.pageCount > 0 && (
                                <span className="meta-item">
                                  <FaFilePdf /> {chapter.pageCount} pages
                                </span>
                              )}
                              <a
                                href={chapter.pdfFile}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="view-link"
                              >
                                View PDF
                              </a>
                            </div>
                          </div>
                        </div>

                        <div className="chapter-actions">
                          <button
                            type="button"
                            className="btn-icon btn-danger"
                            onClick={() => handleDeleteChapter(chapter._id)}
                            disabled={loading}
                            title="Delete chapter"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default ChapterManager;
